import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Package,
  RefreshCw,
  Search,
  Store
} from 'lucide-react';
import { ordersAPI, shopsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const isReceived = (record) =>
  Boolean(record.received ?? Number(record.shop_quantity) > 0);

const ReceivedCheck = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canFilterByShop = user?.role === 'admin' || user?.role === 'manager';
  const [filters, setFilters] = useState({
    search: '',
    shop_id: '',
    received: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 30
  });

  const { data: shopsData } = useQuery({
    queryKey: ['shops'],
    queryFn: () => shopsAPI.getAll().then(res => res.data),
    enabled: canFilterByShop
  });

  const shops = Array.isArray(shopsData?.shops) ? shopsData.shops : [];

  const {
    data,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['replenishment-receipts', filters],
    queryFn: () => ordersAPI.getReplenishmentReceipts(filters).then(res => res.data)
  });

  const records = Array.isArray(data?.records) ? data.records : [];
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1, limit: filters.limit };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      shop_id: '',
      received: '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 30
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const receivedCount = records.filter(isReceived).length;
  const notReceivedCount = records.filter((r) => !isReceived(r)).length;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <ClipboardCheck className="h-8 w-8 mr-3 text-emerald-600" />
            Received Check
          </h1>
          <p className="text-gray-600 mt-2">
            Perfume sales at your shop. Items highlighted in red are not received yet
            (shop stock is still zero).
          </p>
          {user?.shop_id && (
            <p className="text-sm text-gray-500 mt-1 flex items-center">
              <Store className="h-4 w-4 mr-1" />
              Showing perfume sales for your assigned shop
            </p>
          )}
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['replenishment-receipts'] })}
          className="inline-flex items-center justify-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50"
          disabled={isFetching}
        >
          <RefreshCw className={`h-5 w-5 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">On this page</p>
          <p className="text-2xl font-semibold text-gray-900">{records.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Received</p>
          <p className="text-2xl font-semibold text-emerald-700">{receivedCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Not received</p>
          <p className="text-2xl font-semibold text-red-600">{notReceivedCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(event) => handleFilterChange('search', event.target.value)}
                placeholder="Search perfume, SKU, or order number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.received}
              onChange={(event) => handleFilterChange('received', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">All</option>
              <option value="true">Received</option>
              <option value="false">Not received</option>
            </select>
          </div>
          {canFilterByShop && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
              <select
                value={filters.shop_id}
                onChange={(event) => handleFilterChange('shop_id', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">All Shops</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>{shop.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(event) => handleFilterChange('start_date', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(event) => handleFilterChange('end_date', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Perfume sales</h2>
            <p className="text-sm text-gray-500">{pagination.total || 0} records found</p>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-4 w-4 mr-1" />
            Red = not received yet
          </span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading perfume sale records...</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No perfume sale records found</p>
            <p className="text-sm text-gray-500 mt-1">
              Sold perfumes for your shop will appear here.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Perfume
                    </th>
                    {canFilterByShop && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shop
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sale
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sold qty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shop stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => {
                    const received = isReceived(record);
                    return (
                      <tr
                        key={record.order_item_id}
                        className={`hover:bg-gray-50 ${!received ? 'bg-red-50/60' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{record.product_name}</div>
                          <div className="text-sm text-gray-500">
                            {record.sku || 'No SKU'}
                            {record.size ? ` · ${record.size}` : ''}
                          </div>
                        </td>
                        {canFilterByShop && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{record.shop_name}</div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{record.order_number}</div>
                          <div className="text-sm text-gray-500">{formatDate(record.sale_date)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {record.sold_quantity} Unit
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`text-sm font-semibold ${
                              Number(record.shop_quantity) === 0 ? 'text-red-600' : 'text-emerald-700'
                            }`}
                          >
                            {record.shop_quantity || 0} Unit
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {received ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Received
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="h-3.5 w-3.5 mr-1" />
                              Not received
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden p-4 space-y-4">
              {records.map((record) => {
                const received = isReceived(record);
                return (
                  <div
                    key={record.order_item_id}
                    className={`border rounded-xl p-4 space-y-3 ${
                      received ? 'border-gray-200' : 'border-red-300 bg-red-50/60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{record.product_name}</h3>
                        <p className="text-sm text-gray-500">
                          {record.order_number} · {formatDate(record.sale_date)}
                        </p>
                        {canFilterByShop && (
                          <p className="text-sm text-gray-500">{record.shop_name}</p>
                        )}
                      </div>
                      {received ? (
                        <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Sold: {record.sold_quantity} Unit</span>
                      <span
                        className={
                          Number(record.shop_quantity) === 0 ? 'text-red-600 font-semibold' : 'text-emerald-700'
                        }
                      >
                        Stock: {record.shop_quantity || 0}
                      </span>
                    </div>
                    {!received && (
                      <p className="text-xs font-medium text-red-700">Not received yet</p>
                    )}
                  </div>
                );
              })}
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.page <= 1}
                    onClick={() => handleFilterChange('page', pagination.page - 1)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handleFilterChange('page', pagination.page + 1)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReceivedCheck;
